// definitions
import { StripeObjectMap } from '../../../../../back-end/common/stripeObjectMap'
import { StripeOrgAccountXeroOrgSettings } from '../../../../../back-end/common/stripeOrgAccountXeroOrgSettings'
import {
	StripeOrgAccountXeroOrg,
	AccountStripeOrg,
	StripeProduct,
	StripeTaxRate,
	StripeOrgAccountXeroOrgMapping,
	StripeRuleOperator,
	StripeRule,
	StripeOrgAccountXeroOrgCustomMappingType,
} from '../../../../../back-end/utilities/apiDefinitions'
import { DetailedStripeOrgAccountXeroOrg, Error } from './settings'

const validateEmailAddressFormat = (email: string): boolean => {
	if (!email) {
		return true
	}
	return /^[\w+-.%]+@[\w-.]+\.[A-Za-z]{2,63}$/.test(email)
}

const filterUneditedMappings = (orgMappings: DetailedStripeOrgAccountXeroOrg[]) =>
	orgMappings
		.filter((mapping) => mapping.edited)
		.map((mapping) => ({
			...mapping,
			// these ones we can filter because we never delete on the backend, custom mappings can be deleted so they must all be sent
			stripeProductXeroMappings: mapping.stripeProductXeroMappings?.filter((mapping) => mapping.edited),
			stripeTaxRateXeroTaxRates: mapping.stripeTaxRateXeroTaxRates?.filter((mapping) => mapping.edited),
		}))

const validateMappings = (
	stripeOrgs: (AccountStripeOrg & { products: StripeProduct[]; taxRates: StripeTaxRate[] })[],
	orgMappings: (StripeOrgAccountXeroOrg & { edited?: boolean })[],
	stripeObjectMaps: StripeObjectMap[]
): Error[] => {
	return stripeOrgs
		.filter((stripeOrg) => orgMappings.some((orgMapping) => orgMapping.stripeOrgID === stripeOrg.stripeOrg?.stripeOrgID))
		.reduce((errors: Error[], stripeOrg) => {
			const orgMapping = orgMappings.find((orgMapping) => orgMapping.stripeOrgID === stripeOrg.stripeOrg?.stripeOrgID) ?? {}
			console.log('orgMapping:', orgMapping)
			if (orgMapping && orgMapping.accountXeroOrgID) {
				// Check general settings
				if (!orgMapping.stripeOrgAccountXeroOrgSettings?.zeroPercentXeroTaxRateID) {
					errors.push({
						stripeID: stripeOrg.stripeOrg!.stripeOrgID!,
						element: 'general-settings-mapping',
						message: `General Settings mapping failed for Stripe Org: ${stripeOrg.stripeOrg?.name}`,
						severity: 'error',
					})
				}
				// Check payouts
				const payoutsValidation = validatePayouts(orgMapping)
				errors.push(
					...payoutsValidation.map((e) => ({
						stripeID: stripeOrg.stripeOrg!.stripeOrgID!,
						...e,
					}))
				)
				// Check products
				const productsValidation = validateProducts(stripeOrg.products, orgMapping)
				console.log('productsValidation:', productsValidation)
				errors.push(
					...productsValidation.map((e) => ({
						stripeID: stripeOrg.stripeOrg!.stripeOrgID!,
						...e,
					}))
				)
				// Check tax rates
				const taxRatesValidation = validateTaxRates(stripeOrg.taxRates, orgMapping)
				errors.push(
					...taxRatesValidation.map((e) => ({
						stripeID: stripeOrg.stripeOrg!.stripeOrgID!,
						...e,
					}))
				)
				// Check stripe fees
				const stripeFeesValidation = validateStripeFees(orgMapping)
				errors.push(
					...stripeFeesValidation.map((e) => ({
						stripeID: stripeOrg.stripeOrg!.stripeOrgID!,
						...e,
					}))
				)
				// Check credit notes
				const creditNotesValidation = validateCreditNotes(orgMapping)
				errors.push(
					...creditNotesValidation.map((e) => ({
						stripeID: stripeOrg.stripeOrg!.stripeOrgID!,
						...e,
					}))
				)
				// Check customer balance
				const customerBalanceValidation = validateCustomerBalance(orgMapping)
				errors.push(
					...customerBalanceValidation.map((e) => ({
						stripeID: stripeOrg.stripeOrg!.stripeOrgID!,
						...e,
					}))
				)
				// Check discounts
				const discountsValidation = validateDiscounts(orgMapping)
				errors.push(
					...discountsValidation.map((e) => ({
						stripeID: stripeOrg.stripeOrg!.stripeOrgID!,
						...e,
					}))
				)

				// Check alert email format
				if (orgMapping.stripeOrgAccountXeroOrgSettings && orgMapping.stripeOrgAccountXeroOrgSettings.alertRecipientEmail) {
					if (!validateEmailAddressFormat(orgMapping.stripeOrgAccountXeroOrgSettings.alertRecipientEmail)) {
						errors.push({
							stripeID: stripeOrg.stripeOrg!.stripeOrgID!,
							message: `Invalid alert recipient email format: ${orgMapping.stripeOrgAccountXeroOrgSettings.alertRecipientEmail}`,
							element: 'alertRecipientEmail',
							severity: 'error',
						})
					}
				}

				// Check custom mappings

				//check products and customer tracking separately
				const filteredCustomMappingsProducts = orgMapping.customMappings
					? orgMapping.customMappings.filter((customMapping) => ['Account', 'Item', null].includes(customMapping.type!))
					: []
				if (orgMapping?.stripeOrgAccountXeroOrgMappingType === 'Custom') {
					const customMappingValidation = validateCustomMappings({ ...orgMapping, customMappings: filteredCustomMappingsProducts }, stripeObjectMaps)
					errors.push(
						...customMappingValidation.map((e) => ({
							stripeID: stripeOrg.stripeOrg!.stripeOrgID!,
							...e,
						}))
					)
				}

				const filteredCustomMappingsTracking = orgMapping.customMappings
					? orgMapping.customMappings.filter((customMapping) => ['TrackingCategoryOption'].includes(customMapping.type!))
					: []
				if (filteredCustomMappingsTracking.length) {
					const customMappingValidation = validateCustomMappings({ ...orgMapping, customMappings: filteredCustomMappingsTracking }, stripeObjectMaps)
					errors.push(
						...customMappingValidation.map((e) => ({
							stripeID: stripeOrg.stripeOrg!.stripeOrgID!,
							...e,
						}))
					)
				}
				// Check invoice periods
				const invoicePeriodsValidation = validateInvoicePeriods(orgMapping)
				errors.push(
					...invoicePeriodsValidation.map((e) => ({
						stripeID: stripeOrg.stripeOrg!.stripeOrgID!,
						...e,
					}))
				)
				// Check payments
				const paymentsValidation = validatePayments(orgMapping)
				errors.push(
					...paymentsValidation.map((e) => ({
						stripeID: stripeOrg.stripeOrg!.stripeOrgID!,
						...e,
					}))
				)
				// Check shipping
				const shippingValidation = validateShipping()
				errors.push(
					...shippingValidation.map((e) => ({
						stripeID: stripeOrg.stripeOrg!.stripeOrgID!,
						...e,
					}))
				)
				// Check credit grants
				const creditGrantsValidation = validateCreditGrants(orgMapping)
				errors.push(
					...creditGrantsValidation.map((e) => ({
						stripeID: stripeOrg.stripeOrg!.stripeOrgID!,
						...e,
					}))
				)
			}

			return errors
		}, [])
}

export interface ValidationError {
	message: string
	element: string
	id?: string
	severity: 'error' | 'warning'
}

function validatePayouts(orgMapping: StripeOrgAccountXeroOrg): ValidationError[] {
	const errors: ValidationError[] = []
	if (orgMapping.stripeOrgAccountXeroOrgProcessingType !== 'Standard') {
		if (
			!(
				(orgMapping.stripeOrgAccountXeroOrgSettings?.rollupsXeroContactID || orgMapping.stripeOrgAccountXeroOrgSettings?.rollupsXeroContactXeroID) &&
				orgMapping.stripeOrgAccountXeroOrgSettings.rollupsPositiveXeroAccountID &&
				orgMapping.stripeOrgAccountXeroOrgSettings.rollupsPositiveXeroTaxRateID &&
				orgMapping.stripeOrgAccountXeroOrgSettings.rollupsNegativeXeroAccountID &&
				orgMapping.stripeOrgAccountXeroOrgSettings.rollupsNegativeXeroTaxRateID
			)
		) {
			errors.push({ message: 'Payouts mapping incomplete', element: 'payout-mapping', severity: 'error' })
		}
	}
	return errors
}

function validateProducts(products: StripeProduct[], orgMapping: StripeOrgAccountXeroOrg): ValidationError[] {
	const errors: ValidationError[] = []
	if (orgMapping.stripeOrgAccountXeroOrgMappingType !== 'Custom') {
		products.forEach((product) => {
			const found = orgMapping.stripeProductXeroMappings!.some(
				(productMapping) =>
					productMapping.stripeProductID === product.stripeProductID &&
					(productMapping.ignoreInvoices || productMapping[`xero${orgMapping.stripeOrgAccountXeroOrgMappingType as 'Account' | 'Item'}ID`])
			)
			if (!found) {
				errors.push({
					message: `Product mapping missing for product: ${product.name}`,
					element: 'account-product-mapping',
					id: product.stripeID,
					severity: 'warning',
				})
			}
		})
	}
	return errors
}

function validateTaxRates(taxRates: StripeTaxRate[], orgMapping: StripeOrgAccountXeroOrg): ValidationError[] {
	const errors: ValidationError[] = []
	if (orgMapping.stripeOrgAccountXeroOrgSettings?.taxAsLineItem) {
		if (!(orgMapping.stripeOrgAccountXeroOrgSettings!.taxXeroAccountID && orgMapping.stripeOrgAccountXeroOrgSettings!.taxXeroTaxRateID)) {
			errors.push({ message: 'Tax as line item mapping incomplete', element: 'tax-settings-mapping', severity: 'error' })
		}
	} else {
		taxRates.forEach((taxRate) => {
			const found = orgMapping.stripeTaxRateXeroTaxRates!.some((taxRateMapping) => taxRateMapping.stripeTaxRateID === taxRate.stripeTaxRateID)
			if (!found) {
				errors.push({
					message: `Tax rate mapping missing for tax rate: ${taxRate.name}`,
					element: 'tax-settings-mapping',
					id: taxRate.stripeTaxRateID,
					severity: 'warning',
				})
			}
		})
	}
	return errors
}

function validateStripeFees(orgMapping: StripeOrgAccountXeroOrg): ValidationError[] {
	const errors: ValidationError[] = []
	if (orgMapping.stripeOrgAccountXeroOrgProcessingType === 'Standard') {
		if (orgMapping.stripeOrgAccountXeroOrgSettings?.stripeFeesBillsEnabled) {
			if (
				!(
					(orgMapping.stripeOrgAccountXeroOrgSettings.stripeFeesXeroContactID ||
						orgMapping.stripeOrgAccountXeroOrgSettings.stripeFeesXeroContactXeroID) &&
					orgMapping.stripeOrgAccountXeroOrgSettings.stripeFeesXeroAccountID &&
					orgMapping.stripeOrgAccountXeroOrgSettings.stripeFeesXeroTaxRateID
				)
			) {
				errors.push({ message: 'Stripe Fees mapping incomplete', element: 'stripe-fees-mapping', severity: 'error' })
			}
		}
	} else {
		if (!(orgMapping.stripeOrgAccountXeroOrgSettings?.stripeFeesXeroAccountID && orgMapping.stripeOrgAccountXeroOrgSettings.stripeFeesXeroTaxRateID)) {
			errors.push({ message: 'Stripe Fees mapping incomplete', element: 'stripe-fees-mapping', severity: 'error' })
		}
	}
	return errors
}

function validateCreditGrants(orgMapping: StripeOrgAccountXeroOrg): ValidationError[] {
	const errors: ValidationError[] = []
	if (orgMapping.stripeOrgAccountXeroOrgProcessingType === 'Standard' && orgMapping.stripeOrgAccountXeroOrgSettings?.creditGrantsEnabled) {
		if (
			orgMapping.stripeOrgAccountXeroOrgSettings.creditGrantsUseSingleXeroAccount &&
			!orgMapping.stripeOrgAccountXeroOrgSettings?.creditGrantsXeroAccountID
		) {
			errors.push({ message: 'Credit Grants mapping incomplete', element: 'credit-grants-mapping', severity: 'error' })
		}
	}
	return errors
}

function validateCustomerBalance(orgMapping: StripeOrgAccountXeroOrg): ValidationError[] {
	const errors: ValidationError[] = []
	if (
		orgMapping.stripeOrgAccountXeroOrgSettings?.customerBalanceEnabled &&
		(!orgMapping.stripeOrgAccountXeroOrgSettings.customerBalanceXeroAccountID || !orgMapping.stripeOrgAccountXeroOrgSettings.customerBalanceXeroTaxRateID)
	) {
		errors.push({ message: 'Customer balance mapping incomplete', element: 'customer-balance-mapping', severity: 'error' })
	}
	return errors
}

function validateDiscounts(orgMapping: StripeOrgAccountXeroOrg): ValidationError[] {
	const errors: ValidationError[] = []
	if (orgMapping.stripeOrgAccountXeroOrgSettings?.discountsAsLineItem && !orgMapping.stripeOrgAccountXeroOrgSettings.discountsXeroAccountID) {
		errors.push({ message: 'Discounts mapping incomplete', element: 'discounts-mapping', severity: 'error' })
	}
	return errors
}

function validateCustomMappings(orgMapping: StripeOrgAccountXeroOrg, stripeObjectMaps: StripeObjectMap[]): ValidationError[] {
	const errors: ValidationError[] = []
	if (orgMapping.stripeOrgAccountXeroOrgMappingType === 'Custom') {
		if (
			!orgMapping.stripeOrgAccountXeroOrgSettings?.customMappingDefaultXeroAccountID &&
			!orgMapping.stripeOrgAccountXeroOrgSettings?.customMappingDefaultXeroItemID &&
			!orgMapping.stripeOrgAccountXeroOrgSettings?.customMappingDefaultIgnoreInvoices
		) {
			errors.push({ message: 'Custom mapping default Xero account/item missing', element: 'account-product-mapping', severity: 'error' })
		}
		if (!orgMapping.customMappings || orgMapping.customMappings.length === 0) {
			errors.push({ message: 'No custom mappings defined', element: 'account-product-mapping', severity: 'error' })
		}
	}
	const customMappingType = orgMapping.customMappings?.[0].type === 'TrackingCategoryOption' ? 'tracking-categories' : 'account-product-mapping'
	orgMapping.customMappings?.forEach((customMapping) => {
		if (!customMapping.type && !customMapping.ignoreInvoices) {
			errors.push({
				message: 'Custom mapping type missing',
				element: customMappingType,
				id: customMapping.stripeOrgAccountXeroOrgMappingID,
				severity: 'error',
			})
		}
		if (customMappingType === 'account-product-mapping' && !(customMapping.xeroAccountID || customMapping.xeroItemID) && !customMapping.ignoreInvoices) {
			errors.push({
				message: 'Custom mapping Xero account/item missing',
				element: customMappingType,
				id: customMapping.stripeOrgAccountXeroOrgMappingID,
				severity: 'error',
			})
		}
		if (customMappingType === 'tracking-categories' && !customMapping.xeroTrackingCategoryOptionID) {
			errors.push({
				message: 'Xero tracking category missing',
				element: customMappingType,
				id: customMapping.stripeOrgAccountXeroOrgMappingID,
				severity: 'error',
			})
		}
		const ruleErrors = validateStripeRules(customMapping.stripeRules, stripeObjectMaps)
		if (ruleErrors.length > 0) {
			errors.push(
				...ruleErrors.map(() => ({
					message: customMappingType === 'account-product-mapping' ? 'Custom mapping rule invalid' : 'Tracking category rule invalid',
					element: customMappingType,
					id: customMapping.stripeOrgAccountXeroOrgMappingID,
					severity: 'error' as const,
				}))
			)
		}
	})
	return errors
}

function validateInvoicePeriods(orgMapping: StripeOrgAccountXeroOrg): ValidationError[] {
	const errors: ValidationError[] = []
	if (orgMapping.stripeOrgAccountXeroOrgSettings?.invoiceLineItemDatesEnabled && !orgMapping.stripeOrgAccountXeroOrgSettings.invoiceLineItemDateFormat) {
		errors.push({ message: 'Invoice periods date format missing', element: 'invoice-periods-mapping', severity: 'error' })
	}
	return errors
}

function validatePayments(orgMapping: StripeOrgAccountXeroOrg): ValidationError[] {
	const errors: ValidationError[] = []
	if (orgMapping.stripeOrgAccountXeroOrgProcessingType === 'Standard' && orgMapping.stripeOrgAccountXeroOrgSettings?.paymentsEnabled) {
		if (
			!(
				(orgMapping.stripeOrgAccountXeroOrgSettings.paymentsXeroContactID || orgMapping.stripeOrgAccountXeroOrgSettings.paymentsXeroContactXeroID) &&
				(orgMapping.stripeOrgAccountXeroOrgSettings.paymentsXeroAccountID || orgMapping.stripeOrgAccountXeroOrgSettings.paymentsXeroItemID) &&
				orgMapping.stripeOrgAccountXeroOrgSettings.paymentsXeroTaxRateID
			)
		) {
			errors.push({ message: 'Payments mapping incomplete', element: 'payments-mapping', severity: 'error' })
		}
	}
	return errors
}

function validateShipping(): ValidationError[] {
	const errors: ValidationError[] = []
	// currently no validation for shipping
	// if (orgMapping.stripeOrgAccountXeroOrgSettings?.shippingXeroAccountID) {
	// 	errors.push({ message: 'Shipping mapping incomplete', element: 'shipping-mapping', severity: 'error' })
	// }
	return errors
}

function validateCreditNotes(orgMapping: StripeOrgAccountXeroOrg): ValidationError[] {
	const errors: ValidationError[] = []
	if (orgMapping.stripeOrgAccountXeroOrgProcessingType === 'Standard' && orgMapping.stripeOrgAccountXeroOrgSettings?.creditNotesEnabled) {
		if (!orgMapping.stripeOrgAccountXeroOrgSettings?.creditNotesXeroAccountID) {
			errors.push({ message: 'Credit Notes mapping incomplete', element: 'credit-notes-mapping', severity: 'error' })
		}
	}
	return errors
}

// Will need to be modified for different types of mappings
function validateStripeRules(stripeRules: StripeRule[] | undefined, stripeObjectMaps: StripeObjectMap[]): ValidationError[] {
	const errors: ValidationError[] = []
	if (!stripeRules || stripeRules.length === 0) {
		return [{ message: 'No rules defined', element: 'custom-mapping-rule', severity: 'error' }]
	}
	stripeRules.forEach((stripeRule) => {
		// See if there's an associated object map, if so, use its value type to check the correct property
		const objectMap = stripeObjectMaps.find((map) => map.propertyName === stripeRule.key)
		const stripeRuleValueKey = (objectMap ? `value${objectMap.valueType}` : 'valueVARCHAR') as keyof StripeRule
		// stripeRule.value of null is now valid with additional advanced mapping
		if (
			!stripeRule.stripeMappingTypeID ||
			!stripeRule.key ||
			!stripeRule.operator ||
			stripeRule[stripeRuleValueKey] === undefined ||
			stripeRule[stripeRuleValueKey] === ''
		) {
			errors.push({ message: 'Stripe rule incomplete', element: 'custom-mapping-rule', id: stripeRule.stripeRuleID, severity: 'error' })
		}
	})
	return errors
}

const handleOrgAdd = (
	orgMappings: DetailedStripeOrgAccountXeroOrg[],
	updateOrgMappings: React.Dispatch<React.SetStateAction<DetailedStripeOrgAccountXeroOrg[]>>,
	stripeOrgID: string,
	accountXeroOrgID: string
) => {
	const updatedOrgMappings = orgMappings.concat([
		{
			stripeOrgAccountXeroOrgMappingType: 'Account',
			stripeOrgAccountXeroOrgProcessingType: 'Standard',
			stripeOrgID: stripeOrgID,
			accountXeroOrgID: accountXeroOrgID,
			stripeProductXeroMappings: [],
			stripeTaxRateXeroTaxRates: [],
			customMappings: [],
			edited: true,
		},
	])
	updateOrgMappings(updatedOrgMappings)
}

const handleOrgUpdate = (
	updateOrgMappings: React.Dispatch<React.SetStateAction<DetailedStripeOrgAccountXeroOrg[]>>,
	stripeOrgID: string,
	name: string,
	value: string
) => {
	updateOrgMappings((prev) =>
		prev
			.map((mapping) =>
				mapping.stripeOrgID === stripeOrgID
					? {
							...mapping,
							[name]: value,
							edited: true,
					  }
					: mapping
			)
			.filter((mapping) => mapping.stripeOrgAccountXeroOrgID || mapping.accountXeroOrgID)
	)
}

const handleProductAdd = (
	orgMappings: DetailedStripeOrgAccountXeroOrg[],
	updateOrgMappings: React.Dispatch<React.SetStateAction<DetailedStripeOrgAccountXeroOrg[]>>,
	stripeOrgID: string,
	stripeProductID: string,
	name: string,
	value: string
) => {
	const updatedOrgMappings = orgMappings.map((mapping) =>
		mapping.stripeOrgID === stripeOrgID
			? {
					...mapping,
					stripeProductXeroMappings: mapping.stripeProductXeroMappings!.concat({
						stripeProductID: stripeProductID,
						...(value === 'ignoreInvoices'
							? {
									ignoreInvoices: true,
							  }
							: {
									[name]: value,
							  }),
						edited: true,
					}),
					edited: true,
			  }
			: mapping
	)
	updateOrgMappings(updatedOrgMappings)
}

const handleProductUpdate = (
	orgMappings: DetailedStripeOrgAccountXeroOrg[],
	updateOrgMappings: React.Dispatch<React.SetStateAction<DetailedStripeOrgAccountXeroOrg[]>>,
	stripeOrgID: string,
	stripeProductID: string,
	name: string,
	value: string
) => {
	const updatedOrgMappings = orgMappings.map((mapping) =>
		mapping.stripeOrgID === stripeOrgID
			? {
					...mapping,
					stripeProductXeroMappings: mapping.stripeProductXeroMappings!.map((productMapping) =>
						productMapping.stripeProductID === stripeProductID
							? {
									...productMapping,
									...(value === 'ignoreInvoices'
										? {
												ignoreInvoices: true,
												xeroAccountID: null,
												xeroItemID: null,
										  }
										: value && name === 'xeroAccountID'
										? {
												xeroAccountID: value,
												xeroItemID: null,
												ignoreInvoices: false,
										  }
										: value && name === 'xeroItemID'
										? {
												xeroItemID: value,
												xeroAccountID: null,
												ignoreInvoices: false,
										  }
										: {
												xeroAccountID: null,
												xeroItemID: null,
												ignoreInvoices: false,
										  }),
									edited: true,
							  }
							: productMapping
					),
					edited: true,
					error: false,
			  }
			: mapping
	)
	updateOrgMappings(updatedOrgMappings)
}

const handleApplyToAllProducts = (
	orgMappings: DetailedStripeOrgAccountXeroOrg[],
	updateOrgMappings: React.Dispatch<React.SetStateAction<DetailedStripeOrgAccountXeroOrg[]>>,
	stripeProducts: StripeProduct[],
	stripeOrgID: string,
	name: string,
	value: string
) => {
	const updatedOrgMappings = orgMappings.map((mapping) =>
		mapping.stripeOrgID === stripeOrgID
			? {
					...mapping,
					stripeProductXeroMappings: stripeProducts.map((product) => {
						const existingMapping = mapping.stripeProductXeroMappings?.find((mapping) => mapping.stripeProductID === product.stripeProductID)
						return existingMapping
							? {
									...existingMapping,
									...(value === 'ignoreInvoices'
										? {
												ignoreInvoices: true,
												xeroAccountID: null,
												xeroItemID: null,
										  }
										: value && name === 'xeroAccountID'
										? {
												xeroAccountID: value,
												xeroItemID: null,
												ignoreInvoices: false,
										  }
										: value && name === 'xeroItemID'
										? {
												xeroItemID: value,
												xeroAccountID: null,
												ignoreInvoices: false,
										  }
										: {
												xeroAccountID: null,
												xeroItemID: null,
												ignoreInvoices: false,
										  }),
									// [name]: value,
									edited: true,
							  }
							: {
									stripeProductID: product.stripeProductID,
									...(value === 'ignoreInvoices'
										? {
												ignoreInvoices: true,
										  }
										: {
												[name]: value,
										  }),
									edited: true,
							  }
					}),
					edited: true,
			  }
			: mapping
	)
	updateOrgMappings(updatedOrgMappings)
}

const handleTaxRateAdd = (
	orgMappings: DetailedStripeOrgAccountXeroOrg[],
	updateOrgMappings: React.Dispatch<React.SetStateAction<DetailedStripeOrgAccountXeroOrg[]>>,
	stripeOrgID: string,
	stripeTaxRateID: string,
	name: string,
	value: string
) => {
	const updatedOrgMappings = orgMappings.map((mapping) =>
		mapping.stripeOrgID === stripeOrgID
			? {
					...mapping,
					stripeTaxRateXeroTaxRates: mapping.stripeTaxRateXeroTaxRates!.concat({
						stripeTaxRateID: stripeTaxRateID,
						[name]: value,
						edited: true,
					}),
					edited: true,
			  }
			: mapping
	)
	updateOrgMappings(updatedOrgMappings)
}

const handleTaxRateUpdate = (
	orgMappings: DetailedStripeOrgAccountXeroOrg[],
	updateOrgMappings: React.Dispatch<React.SetStateAction<DetailedStripeOrgAccountXeroOrg[]>>,
	stripeOrgID: string,
	stripeTaxRateID: string,
	name: string,
	value: string
) => {
	const updatedOrgMappings = orgMappings.map((mapping) =>
		mapping.stripeOrgID === stripeOrgID
			? {
					...mapping,
					stripeTaxRateXeroTaxRates: mapping.stripeTaxRateXeroTaxRates!.map((taxRateMapping) =>
						taxRateMapping.stripeTaxRateID === stripeTaxRateID
							? {
									...taxRateMapping,
									xeroTaxRateID: value,
									edited: true,
							  }
							: taxRateMapping
					),
					edited: true,
					error: false,
			  }
			: mapping
	)
	updateOrgMappings(updatedOrgMappings)
}

const handleSettingsUpdate = (
	orgMappings: DetailedStripeOrgAccountXeroOrg[],
	updateOrgMappings: React.Dispatch<React.SetStateAction<DetailedStripeOrgAccountXeroOrg[]>>,
	stripeOrgID: string,
	values: Partial<StripeOrgAccountXeroOrgSettings> //{ [name: string]: string | boolean | XeroAPIContact | undefined | null }
) => {
	updateOrgMappings((prev) =>
		prev.map((mapping) =>
			mapping.stripeOrgID === stripeOrgID
				? {
						...mapping,
						stripeOrgAccountXeroOrgSettings: {
							...mapping.stripeOrgAccountXeroOrgSettings,
							...values,
						},
						edited: true,
						error: false,
				  }
				: mapping
		)
	)
}

const handleCustomMappingUpdate = (
	orgMappings: DetailedStripeOrgAccountXeroOrg[],
	updateOrgMappings: React.Dispatch<React.SetStateAction<DetailedStripeOrgAccountXeroOrg[]>>,
	stripeOrgID: string,
	customMappings: Partial<StripeOrgAccountXeroOrgMapping>[]
) => {
	const updatedOrgMappings = orgMappings.map((mapping) =>
		mapping.stripeOrgID === stripeOrgID
			? {
					...mapping,
					customMappings: customMappings,
					edited: true,
					error: false,
			  }
			: mapping
	)
	updateOrgMappings(updatedOrgMappings)
}
const handleAddMapping = (customMappings: StripeOrgAccountXeroOrgMapping[], types: StripeOrgAccountXeroOrgCustomMappingType[]) => {
	const mappingID = `temp_${Math.random()}`
	const newMapping = {
		stripeOrgAccountXeroOrgMappingID: mappingID,
		type: types[0],
		order: customMappings.filter((customMapping) => types.includes(customMapping.type!)).length,
		stripeRules: [
			{
				stripeOrgAccountXeroOrgMappingID: mappingID,
				stripeRuleID: `temp_${Math.random()}`,
				operator: '=' as StripeRuleOperator,
				edited: true,
			},
		],
		edited: true,
	}
	return newMapping
}

const handleMappingUpdate = (
	customMappings: StripeOrgAccountXeroOrgMapping[],
	stripeOrgAccountXeroOrgMappingID: string,
	values: Partial<StripeOrgAccountXeroOrgMapping>
) => {
	const updatedMappings = customMappings.map((customMapping) =>
		customMapping.stripeOrgAccountXeroOrgMappingID === stripeOrgAccountXeroOrgMappingID
			? {
					...customMapping,
					...values,
					edited: true,
			  }
			: customMapping
	)
	return updatedMappings
}

const handleAddRule = (stripeOrgAccountXeroOrgMappingID: string) => {
	const newRule = {
		stripeOrgAccountXeroOrgMappingID: stripeOrgAccountXeroOrgMappingID,
		stripeRuleID: `temp_${Math.random()}`,
		operator: '=' as StripeRuleOperator,
		edited: true,
	}
	return newRule
}

const handleRuleUpdate = (customMapping: StripeOrgAccountXeroOrgMapping, stripeRuleID: string, values: Partial<StripeRule>) => {
	const updatedRules = customMapping.stripeRules?.map((stripeRule) =>
		stripeRule.stripeRuleID === stripeRuleID
			? {
					...stripeRule,
					...values,
					edited: true,
			  }
			: stripeRule
	)
	return updatedRules
}

// After deletion, pass in -1 as moveToIndex, function will just update order property of remaining elements
const handleReorder = (
	customMappings: StripeOrgAccountXeroOrgMapping[],
	stripeOrgAccountXeroOrgMappingID: string,
	moveToIndex: number,
	types: StripeOrgAccountXeroOrgCustomMappingType[]
) => {
	let elementsToReorder = customMappings.filter((customMapping) => types.includes(customMapping.type!))
	const movedFromIndex =
		moveToIndex >= 0 ? elementsToReorder.findIndex((mapping) => mapping.stripeOrgAccountXeroOrgMappingID === stripeOrgAccountXeroOrgMappingID) : -1
	if (movedFromIndex >= 0) {
		const element = elementsToReorder[movedFromIndex]
		const otherElements = elementsToReorder.filter((customMapping) => customMapping.stripeOrgAccountXeroOrgMappingID !== stripeOrgAccountXeroOrgMappingID)
		const beforeMovedElement = otherElements.slice(0, moveToIndex)
		const afterMovedElement = otherElements.slice(moveToIndex)
		elementsToReorder = beforeMovedElement.concat([element]).concat(afterMovedElement)
	}

	return elementsToReorder
		.map((mapping, index) => ({ ...mapping, order: index } as StripeOrgAccountXeroOrgMapping))
		.concat(customMappings.filter((customMapping) => !types.includes(customMapping.type!)))
}

export {
	filterUneditedMappings,
	validateMappings,
	handleOrgAdd,
	handleOrgUpdate,
	handleProductAdd,
	handleProductUpdate,
	handleApplyToAllProducts,
	handleTaxRateAdd,
	handleTaxRateUpdate,
	handleSettingsUpdate,
	handleCustomMappingUpdate,
	handleAddMapping,
	handleMappingUpdate,
	handleAddRule,
	handleRuleUpdate,
	handleReorder,
	validateEmailAddressFormat,
}
